import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AncreVue, ConversationVue, ConversationsCentrePort, PersonneVue } from '@taki/ui';
import type { ContexteOuvert } from './contexte.js';
import type { ConversationResume } from './sources.js';

/**
 * LES SOURCES CANAUX, MESSAGES DIRECTS ET ICI DU CENTRE (vague finale du
 * 05/10/2026, contrat `docs/socle/conversations.md`) : les listes du rail, les
 * non lus GELES par conversation (G15 : un `count()` serveur par conversation,
 * jamais un filtre en memoire), le chatter de l'objet courant, et les gestes
 * de creation. Tout passe par le port monte par l'hote
 * (`ConversationsCentrePort`) ; sans port, les sources restent des emplacements.
 */

/**
 * L'ancre du chatter de ce qui est ouvert a l'ecran : la FICHE seulement (R9
 * du soir : on ne chatte que sur une fiche ; ni porte, ni app).
 */
export function ancreDuContexte(contexte: ContexteOuvert): AncreVue | null {
  if (contexte.recordId && contexte.family) return { family: contexte.family, recordId: contexte.recordId };
  return null;
}

/** Une ancre de fiche, ou rien : une ancre de porte ou d'app demandee (ancien bouton, vieux lien) est ignoree (R9). */
export function ancreDeFiche(ancre: AncreVue | null): AncreVue | null {
  return ancre !== null && 'family' in ancre ? ancre : null;
}

/** La reference d'epinglage du socle : `lia:<sujetId>` ou `conversation:<id>`. */
export function refDEpingle(source: 'lia' | 'conversation', id: string): string {
  return `${source}:${id}`;
}

/** Deux ancres designent le meme objet. */
export function memeAncre(a: AncreVue | null, b: AncreVue | null): boolean {
  if (a === null || b === null) return a === b;
  if ('family' in a) return 'family' in b && a.family === b.family && a.recordId === b.recordId;
  return !('family' in b) && a.appId === b.appId && (a.doorId ?? '') === (b.doorId ?? '');
}

/** Ce que l'en-tete dit de la fiche d'un chatter : « Ada Lovelace ». */
export function titreDeLAncre(ancre: AncreVue | null, contexte: ContexteOuvert): string {
  if (ancre === null || !('family' in ancre)) return 'Ici';
  return memeAncre(ancre, ancreDuContexte(contexte)) ? contexte.recordTitle?.trim() || 'Fiche' : 'Fiche';
}

/** Le nom d'un direct : les AUTRES participants, nommes par l'annuaire. */
/**
 * P22 : l'autre personne d'un direct, pour son avatar dans la liste (la
 * premiere des autres participants ; un direct a soi-meme montre soi-meme).
 */
export function personneDeDirect(conversation: ConversationVue, moi: string | null, personnes: readonly PersonneVue[], initiales: (nom: string) => string): { initiales: string; photoURL?: string | null } {
  const autre = conversation.participants.find((personId) => personId !== moi) ?? conversation.participants[0];
  const p = personnes.find((x) => x.personId === autre);
  const nom = p ? (p.displayName.trim() || p.email) : 'Compte inconnu';
  return { initiales: initiales(nom), photoURL: p?.photoURL ?? null };
}

export function titreDeDirect(conversation: ConversationVue, moi: string | null, noms: ReadonlyMap<string, string>): string {
  const autres = conversation.participants.filter((personId) => personId !== moi);
  const liste = (autres.length > 0 ? autres : conversation.participants).map((personId) => noms.get(personId) ?? 'Compte inconnu');
  return liste.join(', ') || 'Message direct';
}

/**
 * Les conversations du rail pour un groupe, avec leurs non lus geles, leur
 * reference d'epinglage et ce que j'ai le droit d'en faire (R4) : un canal se
 * renomme et se supprime si je le gere, un direct se supprime POUR MOI et ne
 * se renomme pas.
 */
export function resumesDeConversations(
  liste: readonly ConversationVue[], source: 'canaux' | 'directs', nonLus: Readonly<Record<string, number>>,
  titre: (conversation: ConversationVue) => string, epingles: ReadonlySet<string>, gerables: ReadonlySet<string> = new Set(),
  personneDe?: (conversation: ConversationVue) => ConversationResume['personne'],
): ConversationResume[] {
  return liste.map((conversation): ConversationResume => {
    const ref = refDEpingle('conversation', conversation.id);
    const personne = source === 'directs' ? personneDe?.(conversation) : undefined;
    return {
      ...(personne ? { personne } : {}),
      id: conversation.id, source, titre: titre(conversation), nonLus: nonLus[conversation.id] ?? 0, ref,
      glyphe: source === 'canaux' ? (conversation.prive ? 'cadenas' : 'hash') : 'presence', epinglee: epingles.has(ref),
      renommable: source === 'canaux' && gerables.has(conversation.id),
      // P18 : un direct ne se supprime pas ; seul un canal gere se supprime (archive pour tous).
      supprimable: source === 'canaux' && gerables.has(conversation.id),
    };
  });
}

export interface ChatterEpingle { conversation: ConversationVue; titre: string }

export interface SourcesConversations {
  pret: boolean;
  canaux: ConversationVue[];
  directs: ConversationVue[];
  /** Les non lus par conversation, comptes par le serveur a chaque changement de lecture. */
  nonLus: Readonly<Record<string, number>>;
  /** L'ancre de l'objet courant (contexte du shell, ou demandee par `ouvrirCentre`), et son chatter s'il existe deja. */
  ancreIci: AncreVue | null;
  chatterIci: ConversationVue | null;
  chatterIciEnChargement: boolean;
  chatterIciErreur: boolean;
  /** L'objet demande par `ouvrirCentre` (bouton Chatter, notification), avec son titre s'il est connu. */
  fixerAncreIci: (ancre: AncreVue | null, titre?: string) => void;
  /** Le titre donne avec l'ancre demandee (porte ou app, que le contexte de l'ecran ne nomme pas). */
  titreFixe: string | null;
  ouvrirChatterIci: () => Promise<string>;
  marquerLu: (conversationId: string) => void;
  /** personId -> nom affiche, et uid -> nom affiche, depuis l'annuaire. */
  noms: ReadonlyMap<string, string>;
  nomsParUid: ReadonlyMap<string, string>;
  /** La photo des comptes qui en ont une, par uid. */
  photosParUid: ReadonlyMap<string, string>;
  personnes: PersonneVue[];
  /** Les references epinglees PAR MOI (serveur), dans l'ordre d'epinglage. */
  epinglees: readonly string[];
  /** Les chatters de fiche epingles, relus avec le titre de leur fiche (ils ne sont dans aucune liste). */
  chattersEpingles: readonly ChatterEpingle[];
  /** Les canaux que je peux renommer et archiver pour tous (`peutGerer` du socle). */
  canauxGerables: ReadonlySet<string>;
  /** Le chatter de la fiche courante est eteint pour sa base (Tour 3) : on ne peut pas y ecrire. */
  chatterIciEteint: boolean;
  /** La fiche du suivi courant est archivee : le suivi se lit, rien ne s'y ecrit. */
  ficheIciArchivee: boolean;
}

/** Recopie de `MESSAGE_CHATTER_ETEINT` (`@taki/sdk`, Tour 3 du contrat), que le shell n'importe pas. */
export const MESSAGE_CHATTER_ETEINT = 'Le suivi est éteint pour les fiches de cette base : le message n’est pas envoyé.';
/** Une fiche archivee garde son chatter lisible ; le composeur dit pourquoi il est inerte. */
export const MESSAGE_FICHE_ARCHIVEE = 'Fiche archivée : le suivi se lit, rien ne s’y écrit.';

/** La raison pour laquelle le composeur du chatter « Ici » est en lecture seule, ou `null` quand on peut y ecrire. */
export function raisonLectureSeuleIci(etat: { chatterIciEteint: boolean; ficheIciArchivee: boolean }): string | null {
  if (etat.chatterIciEteint) return MESSAGE_CHATTER_ETEINT;
  return etat.ficheIciArchivee ? MESSAGE_FICHE_ARCHIVEE : null;
}

export function useSourcesConversations(
  port: ConversationsCentrePort | null,
  contexte: ContexteOuvert,
  lister: (() => Promise<PersonneVue[]>) | null,
  /** Un compteur qui change a chaque ouverture de la fenetre : l'etat des chatters epingles se relit (Tour 3). */
  relecture = 0,
  pastilles?: import('@taki/ui').PastillesPort,
): SourcesConversations {
  const [canaux, setCanaux] = useState<ConversationVue[]>([]);
  const [directs, setDirects] = useState<ConversationVue[]>([]);
  const [lu, setLu] = useState<Record<string, Date>>({});
  const [nonLusCompat, setNonLus] = useState<Record<string, number>>({});
  const nonLus = pastilles ? Object.fromEntries(Object.entries(pastilles.parConversation).filter(([ref]) => ref.startsWith('conversation:')).map(([ref, n]) => [ref.slice(13), n])) : nonLusCompat;
  const [suivies, setSuivies] = useState<ConversationVue[]>([]);
  const [pret, setPret] = useState(false);
  const [personnes, setPersonnes] = useState<PersonneVue[]>([]);

  useEffect(() => {
    if (port === null) { setPret(true); return; }
    let vivants = 0;
    const arrets = [
      port.observerCanaux((liste) => { setCanaux(liste); if (++vivants >= 2) setPret(true); }, () => setPret(true)),
      port.observerDirects((liste) => { setDirects(liste); if (++vivants >= 2) setPret(true); }, () => setPret(true)),
      ...(port.observerSuivies ? [port.observerSuivies(setSuivies)] : []),
      port.lecture.observer((table) => setLu(table), () => undefined),
    ];
    return () => arrets.forEach((arret) => arret());
  }, [port]);

  useEffect(() => {
    if (lister === null) return;
    let vivant = true;
    lister().then((liste) => { if (vivant) setPersonnes(liste); }, () => undefined);
    return () => { vivant = false; };
  }, [lister]);
  const noms = useMemo(() => new Map(personnes.map((p) => [p.personId, p.displayName.trim() || p.email])), [personnes]);
  const photosParUid = useMemo(() => new Map(personnes.filter((p) => p.userId && p.photoURL).map((p) => [p.userId as string, p.photoURL as string])), [personnes]);
  const nomsParUid = useMemo(() => new Map(personnes.filter((p) => p.userId).map((p) => [p.userId as string, p.displayName.trim() || p.email])), [personnes]);

  // La conversation choisie reste stable quand la navigation de l'app change.
  const ancreEcran = useMemo(() => ancreDuContexte(contexte), [contexte.appId, contexte.doorId, contexte.recordId, contexte.family]);
  const [ancreFixee, setAncreFixee] = useState<AncreVue | null>(ancreEcran);
  const [titreFixe, setTitreFixe] = useState<string | null>(ancreEcran ? contexte.recordTitle?.trim() || null : null);
  const fixerAncreIci = useCallback((ancre: AncreVue | null, titre?: string): void => {
    const fiche = ancreDeFiche(ancre ?? ancreEcran);
    setAncreFixee(fiche);
    setTitreFixe(fiche !== null ? titre?.trim() || (memeAncre(fiche, ancreEcran) ? contexte.recordTitle?.trim() : null) || null : null);
  }, [ancreEcran, contexte.recordTitle]);
  // Une fiche demandee SANS titre (le chatter d'une notification, ouvert depuis une autre app) :
  // son titre se relit par l'hote, jamais l'id a l'ecran (« Ada Lovelace », pas « seed-contact-1 »).
  useEffect(() => {
    if (port?.lireFiche === undefined || ancreFixee === null || !('family' in ancreFixee) || titreFixe !== null) return;
    let vivant = true;
    port.lireFiche(ancreFixee.family, ancreFixee.recordId).then((fiche) => { if (vivant && fiche?.titre) setTitreFixe(fiche.titre); }, () => undefined);
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, ancreFixee, titreFixe]);
  const ancreIci = ancreFixee;
  const cleAncre = ancreIci === null || !('family' in ancreIci) ? '' : `fiche:${ancreIci.family}/${ancreIci.recordId}`;
  const [lectureChatter, setLectureChatter] = useState<{
    cle: string; port: ConversationsCentrePort; conversation: ConversationVue | null; erreur: boolean;
  } | null>(null);
  // Une nouvelle fiche est en chargement des le rendu, avant meme l'effet.
  const lectureCourante = lectureChatter?.cle === cleAncre && lectureChatter.port === port ? lectureChatter : null;
  const chatterIci = lectureCourante?.conversation ?? null;
  const chatterIciEnChargement = port !== null && ancreIci !== null && lectureCourante === null;
  const chatterIciErreur = lectureCourante?.erreur ?? false;
  useEffect(() => {
    setLectureChatter(null);
    if (port === null || ancreIci === null) return;
    let vivant = true;
    port.chatterDe(ancreIci).then(
      (conversation) => { if (vivant) setLectureChatter({ cle: cleAncre, port, conversation, erreur: false }); },
      () => { if (vivant) setLectureChatter({ cle: cleAncre, port, conversation: null, erreur: true }); },
    );
    return () => { vivant = false; };
  }, [port, cleAncre]);
  // Tour 3 : le chatter de la fiche courante est-il allume pour sa base ? Eteint : la fenetre dit pourquoi au lieu du composeur.
  const [chatterIciEteint, setChatterIciEteint] = useState(false);
  useEffect(() => {
    setChatterIciEteint(false);
    if (port?.chatterActif === undefined || ancreIci === null || !('family' in ancreIci)) return;
    let vivant = true;
    port.chatterActif({ family: ancreIci.family, recordId: ancreIci.recordId }).then((actif) => { if (vivant) setChatterIciEteint(!actif); }, () => undefined);
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, cleAncre, relecture]);
  // Une fiche ARCHIVEE garde son chatter lisible mais n'y laisse rien ecrire : une lecture de la fiche, par le port (pas d'ecoute).
  const [ficheIciArchivee, setFicheIciArchivee] = useState(false);
  useEffect(() => {
    setFicheIciArchivee(false);
    if (port?.lireFiche === undefined || ancreIci === null || !('family' in ancreIci)) return;
    let vivant = true;
    port.lireFiche(ancreIci.family, ancreIci.recordId).then((fiche) => { if (vivant) setFicheIciArchivee(fiche?.archivee === true); }, () => undefined);
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, cleAncre, relecture]);
  const ouvrirChatterIci = useCallback(async (): Promise<string> => {
    if (port === null || ancreIci === null) throw new Error('Aucun objet ouvert.');
    if (chatterIci) return chatterIci.id;
    const c = await port.ouvrirChatter(ancreIci);
    setLectureChatter({ cle: cleAncre, port, conversation: c, erreur: false });
    return c.id;
  }, [port, ancreIci, cleAncre, chatterIci]);

  // Les non lus : un count() par conversation, relance quand la liste ou la lecture change (compteur gele sinon).
  const ids = [...canaux.map((c) => c.id), ...directs.map((c) => c.id), ...(chatterIci ? [chatterIci.id] : [])].join('\u0000');
  const luClef = Object.entries(lu).map(([id, date]) => `${id}:${date.getTime()}`).sort().join('\u0000');
  useEffect(() => {
    if (pastilles || port === null || ids === '') return;
    let vivant = true;
    const liste = ids.split('\u0000');
    Promise.all(liste.map(async (id) => [id, await port.lecture.nonLus(id, lu[id]).catch(() => 0)] as const))
      .then((entrees) => { if (vivant) setNonLus(Object.fromEntries(entrees)); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, ids, luClef, !!pastilles]);

  const marquerLu = useCallback((conversationId: string): void => {
    if (port === null) return;
    pastilles?.marquerAffiche(`conversation:${conversationId}`);
  }, [port, pastilles?.marquerAffiche]);

  // R12 : les epingles de la personne, gardes par le serveur ; un chatter de fiche epingle se relit (il n'est dans aucune liste).
  const [epinglees, setEpinglees] = useState<string[]>([]);
  useEffect(() => {
    if (port === null) return;
    return port.epingles.observer((refs) => setEpinglees(refs), () => undefined);
  }, [port]);
  const [chattersEpingles, setChattersEpingles] = useState<ChatterEpingle[]>([]);
  const connus = useMemo(() => new Set([...canaux, ...directs].map((c) => c.id)), [canaux, directs]);
  const aRelire = [...new Set([...suivies.map(c => `conversation:${c.id}`), ...epinglees])].filter((ref) => ref.startsWith('conversation:')).map((ref) => ref.slice('conversation:'.length)).filter((id) => !connus.has(id));
  const cleARelire = aRelire.join('\u0000');
  useEffect(() => {
    if (port === null || aRelire.length === 0) { setChattersEpingles([]); return; }
    let vivant = true;
    Promise.all(aRelire.map(async (id) => {
      const c = await port.lire(id).catch(() => null);
      const fiche = c === null || c.type !== 'chatter' ? null : ancreDeFiche(c.ancre);
      if (c === null || fiche === null || !('family' in fiche)) return null;
      // Une base au chatter ETEINT masque l'epingle (la ref reste, elle revient au rallumage).
      if (port.chatterActif && !(await port.chatterActif({ family: fiche.family, recordId: fiche.recordId }).catch(() => true))) return null;
      const lue = port.lireFiche ? await port.lireFiche(fiche.family, fiche.recordId).catch(() => null) : null;
      return { conversation: c, titre: lue?.titre ?? fiche.recordId };
    })).then((liste) => { if (vivant) setChattersEpingles(liste.filter((c): c is ChatterEpingle => c !== null)); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, cleARelire, relecture]);
  // R4 : les canaux que je peux gerer, demandes au socle a chaque changement de la liste.
  const [canauxGerables, setCanauxGerables] = useState<ReadonlySet<string>>(new Set());
  const cleCanaux = canaux.map((c) => c.id).join('\u0000');
  useEffect(() => {
    if (port === null || canaux.length === 0) { setCanauxGerables(new Set()); return; }
    let vivant = true;
    Promise.all(canaux.map(async (c) => [c.id, await port.peutGerer(c.id).catch(() => false)] as const))
      .then((liste) => { if (vivant) setCanauxGerables(new Set(liste.filter(([, ok]) => ok).map(([id]) => id))); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, cleCanaux]);

  return { epinglees, chattersEpingles, canauxGerables, chatterIciEteint, ficheIciArchivee, chatterIciEnChargement, chatterIciErreur, pret, canaux, directs, nonLus, ancreIci, chatterIci, fixerAncreIci, titreFixe: ancreFixee !== null ? titreFixe : null, ouvrirChatterIci, marquerLu, noms, nomsParUid, photosParUid, personnes };
}
